// @erp/module-cms/schemas — the GALLERY content type wire contracts. A photo-
// gallery item: a single catalogued image (a titled photo with a caption, a body
// description, rights + display metadata, a lightweight collection grouping, and a
// soft-delete → restore → purge trash lifecycle). This is the LAST content type.
// Modelled on the 0004 announcements / 0006 events slices (the soft-delete + status-
// lifecycle content precedent) — but a gallery item is NOT a companion-announcement
// target (a photo does not post to the notice feed), so there is NO createAnnouncement
// routing flag here. Browser-safe: zod + plain data only (fe forms and the gateway
// parse the SAME schemas). Feature-referenced from MBSPSU (READ-ONLY) — rebuilt for
// the target stack, English-first + locale-ready (a translation is a SIBLING ROW under
// a different `locale`, never a *_hi/*_pa column explosion — the 0002 law).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ SECURITY — body_html is stored RAW and is NOT sanitized on write (the 0004     │
// │ announcements law). Sanitization happens ON THE WAY OUT: every /public/*        │
// │ route passes bodyHtml through the allowlist sanitiser (api/sanitize.ts,         │
// │ wired 2026-09-13 per ADR-010 §2.4) before it leaves the ERP. Admin reads        │
// │ carry the raw column; content is authored ONLY by trusted cms:gallery holders.  │
// ├──────────────────────────────────────────────────────────────────────────────┤
// │ DEFERRED (documented, NOT built): dynamic image format/quality conversion       │
// │ (webp/avif transforms — the MBSPSU publicDisplayFormat / *Quality overrides) →  │
// │ the website wave / Cloudflare Images; a private/PIN gate + a public read-split + │
// │ revisions are their own waves. access_level is a stored editorial HINT — the    │
// │ real byte-serve gate lives on the referenced cms_media row's own visibility     │
// │ (C2), NOT here; status is draft|published|archived only (no 'private' yet).     │
// │ media_id is a plain OPAQUE ref to a cms_media id (NO FK, a dangling ref          │
// │ tolerated like the publications cover/pdf); the media's own visibility governs   │
// │ its serve, so there is no join here.                                            │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { BODY_HTML_MAX } from "./announcements.js";
import { PublishingStatus } from "./settings.js";
import { Tag, TAG_MAX, TAGS_MAX } from "./structured.js";

// NOTE: slugify + SLUG_MAX/SLUG_RE are REUSED from the categories slice (the ONE slug
// grammar + deriver) and BODY_HTML_MAX from the announcements slice — one source,
// never a re-declaration.

// ── The access-level vocabulary — an editorial visibility HINT (public | internal |
// restricted — the MBSPSU accessLevel). The actual byte-serve gate lives on the
// referenced cms_media row's own visibility (C2); this token is surfaced on the wire
// so the admin UI + the (future) website can badge/segment, NOT an enforced split. ────
export const GALLERY_ACCESS_LEVELS = ["public", "internal", "restricted"] as const;
export const GalleryAccessLevel = z.enum(GALLERY_ACCESS_LEVELS);
export type GalleryAccessLevel = z.infer<typeof GalleryAccessLevel>;

// ── The publishing status enum — REUSED from settings (draft|published|archived), so
// the gallery lifecycle and the site-wide default_publishing_status can never drift
// onto different value sets (the announcements/events/publications precedent). ─────────
export const GalleryStatus = PublishingStatus;
export type GalleryStatus = z.infer<typeof GalleryStatus>;

// ── Tags — a bounded list of free-text labels serialized to tags_json TEXT. Each tag
// is trimmed + length-bounded; the list is capped so a runaway blob can never bloat the
// row (the events banners[] / attachments[] bounding law). A corrupt blob degrades to []
// at read (the *_json safe-degradation law). THE RULE LIVES IN schemas/structured.ts
// (0018, A4): the gallery's shape became the shape of EVERY tags column (announcements /
// events / publications), so it is declared once there and re-exported here under the
// historical names — a reuse, never a second declaration. ──────────────────────────────
export const GALLERY_TAG_MAX = TAG_MAX;
export const GALLERY_TAGS_MAX = TAGS_MAX;
export const GalleryTag = Tag;
export type GalleryTag = z.infer<typeof GalleryTag>;

// ── The write-side field validators (one source, Create/Update both derive) ─────────
const galleryCore = {
  title: z.string().trim().min(1).max(300),
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** The short plain-text caption (the summary-equivalent for a photo). Nullable to clear. */
  caption: z.string().trim().max(1000).nullable(),
  /** RAW HTML rich description — sanitized ONLY at the website render (HTMLRewriter), never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  /** OPAQUE cms_media id (the photo) — NO FK, a dangling ref tolerated (the publications cover/pdf precedent). Nullable to clear. */
  mediaId: EntityId.nullable(),
  /** Accessibility text for the rendered <img>. Nullable to clear. */
  altText: z.string().trim().max(500).nullable(),
  /** A bounded list of free-text tags (serialized to tags_json). Nullable to clear. */
  tags: z.array(GalleryTag).max(GALLERY_TAGS_MAX).nullable(),
  photographer: z.string().trim().max(300).nullable(),
  copyright: z.string().trim().max(300).nullable(),
  license: z.string().trim().max(300).nullable(),
  /** The editorial visibility hint (the serve gate is the referenced cms_media row's own visibility). */
  accessLevel: GalleryAccessLevel,
  /** A lightweight grouping NAME (a string label); a full album entity is DEFERRED. Nullable to clear. */
  collection: z.string().trim().max(200).nullable(),
  status: GalleryStatus,
  /** Explicit publish instant (epoch-ms). Usually left unset — the service stamps it when status→published. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** FK to a cms_category of type='gallery' — the service validates the cross-entity ref. Nullable to clear. */
  categoryId: EntityId.nullable(),
  /** Manual display ordering within a collection/gallery (ascending). */
  sortOrder: z.number().int().min(0),
  /** Locale-ready seam — v1 writes only 'en' (db-cms 0008). */
  /** The row's locale — a canonical BCP 47 tag (./locale.ts, the locale contract). */
  locale: Locale,
};

/**
 * Create body — title required; slug OPTIONAL (the service derives it via slugify(title),
 * id-fallback on a non-sluggable title). status/locale/accessLevel/sortOrder carry the DDL
 * defaults; published_at is normally derived from the status. There is NO createAnnouncement
 * flag — a gallery item is not a companion-announcement target (unlike events/tenders).
 */
export const CreateCmsGalleryItem = z.object({
  title: galleryCore.title,
  slug: galleryCore.slug.optional(),
  caption: galleryCore.caption.optional(),
  bodyHtml: galleryCore.bodyHtml.optional(),
  mediaId: galleryCore.mediaId.optional(),
  altText: galleryCore.altText.optional(),
  tags: galleryCore.tags.optional(),
  photographer: galleryCore.photographer.optional(),
  copyright: galleryCore.copyright.optional(),
  license: galleryCore.license.optional(),
  accessLevel: galleryCore.accessLevel.default("public"),
  collection: galleryCore.collection.optional(),
  status: galleryCore.status.default("draft"),
  publishedAt: galleryCore.publishedAt.optional(),
  categoryId: galleryCore.categoryId.optional(),
  sortOrder: galleryCore.sortOrder.default(0),
  locale: galleryCore.locale.default("en"),
});
export type CreateCmsGalleryItem = z.infer<typeof CreateCmsGalleryItem>;

/** Update body — a partial PATCH over the same fields (slug stays STABLE on rename; a slug collision still maps to 409). */
export const UpdateCmsGalleryItem = z.object(galleryCore).partial();
export type UpdateCmsGalleryItem = z.infer<typeof UpdateCmsGalleryItem>;

/**
 * List query — keyset cursor+limit + optional ?status ?collection ?accessLevel ?locale
 * filters (soft-deleted rows always excluded). All exact-eq; ?collection matches the grouping name.
 */
export const CmsGalleryItemListQuery = CursorQuery.extend({
  status: GalleryStatus.optional(),
  collection: z.string().trim().min(1).max(200).optional(),
  accessLevel: GalleryAccessLevel.optional(),
  /** Exact-eq on the row's locale (0018) — the admin list narrows to one locale's rows; omitted lists every locale. */
  locale: Locale.optional(),
  /** Opt-in TRASH view — true lists soft-deleted rows only; omitted (the default) lists live rows. Coerced + optional so a stray value never 422s the query. */
  // Self-healing like the FE's ?trashed: only "1"/"true" opt into trash; anything else
  // (absent, "false", "0", garbage) heals to the live list — never flips to trash or 422s.
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsGalleryItemListQuery = z.infer<typeof CmsGalleryItemListQuery>;

/**
 * The wire DTO — the read shape (camelCase; audit actor columns stay server-side).
 * `tags` is the parsed list; `mediaId` is the opaque cms_media ref (may dangle);
 * `deletedAt` surfaces so the admin trash view can tell live rows from trashed ones.
 */
export const CmsGalleryItem = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  title: z.string(),
  caption: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  mediaId: EntityId.nullable(),
  altText: z.string().nullable(),
  tags: z.array(z.string()),
  photographer: z.string().nullable(),
  copyright: z.string().nullable(),
  license: z.string().nullable(),
  accessLevel: GalleryAccessLevel,
  collection: z.string().nullable(),
  status: GalleryStatus,
  publishedAt: EpochMs.nullable(),
  categoryId: EntityId.nullable(),
  sortOrder: z.number().int(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsGalleryItem = z.infer<typeof CmsGalleryItem>;
